import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import Glyph from "./navGlyphs";

/*
 * INBOUND ▸ SCORING — one score per lead, 0-100, and the only scorer.
 *
 * Built against Gabriel's mockup (Scoring.dc.html). The model on the left is
 * the one every surface reads: the after-hours reply's gate, the board card,
 * the record, the Leads column, and the hot / high / medium / low that Slack
 * prints for a new lead (which the old intake classifier used to decide on its
 * own, with thresholds nobody could see — backlog B-013).
 *
 * THE RIGHT-HAND COLUMN IS REAL INBOUND, NOT A MODEL. Every bar and every row is
 * a lead that arrived, scored by the real scorer under the model as it stands on
 * screen, unsaved, and again under the saved one — so the bar across the top
 * can say how many leads a change moves before anything is saved.
 *
 * The mockup's own scoring maths was a placeholder; the numbers here come from
 * src/automations/score.js. Two additions it does not draw: the "Never a lead"
 * phrases (a hard block the old Rules tab edited, which had to live somewhere),
 * and a cohort choice on the re-scored list, because "would this have scored the
 * leads that became clients well?" is the question the list is for.
 */

const LABELS = {
  industry: "Industry",
  headcount: "Headcount",
  title: "Title",
  funding: "Funding",
  age: "Company age",
  country: "Country",
  keywords: "Keywords",
  mailbox: "Mailbox",
};

// How each group turns its rows into points (src/automations/score.js).
const MODE = { title: "best", keywords: "sum" };

const NOTES = {
  industry:
    "Rows match LinkedIn's own industry values, so “Cybersecurity” also catches COMPUTER_NETWORK_SECURITY and “B2B SaaS” catches COMPUTER_SOFTWARE.",
  title: "Spelled-out titles count too, so “CMO” catches Chief Marketing Officer.",
  mailbox: "A penalty, not a block: a founder at a ten-person company genuinely uses gmail.",
  age: "Years since founding. A company with no founding year scores 0 — unknown is not young.",
  country: "An unmatched country scores 0, which says “no signal” rather than “bad”.",
};

const BANDS = ["Low", "Medium", "High", "Hot"];
const BAR = { Low: "#C9C6BB", Medium: "#8F8C83", High: "#3A3933", Hot: "#0A0A0A" };

const COHORTS = [
  { key: "recent", label: "Most recent" },
  { key: "call_scheduled", label: "Had a call" },
  { key: "proposal_sent", label: "Got pricing" },
  { key: "won", label: "Became clients" },
];

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

function bandOf(total, cuts) {
  if (total >= cuts[2]) return "Hot";
  if (total >= cuts[1]) return "High";
  if (total >= cuts[0]) return "Medium";
  return "Low";
}

function ruleLine(name, group, multiplier) {
  const mode = MODE[name] || "one";
  if (mode === "best") return `Takes the best matching row${multiplier ? ", × seniority" : ""}`;
  if (mode === "sum") return `Adds up every match, capped at ${group.cap || 10}`;
  return "Matches one row";
}

/* ------------------------------------------------------------- a row */

// The label commits on blur or Enter rather than per keystroke: the rows are a
// map keyed by label, and renaming on every key would collide with a row that
// shares a prefix and reorder the table under the cursor.
function Row({ label, points, onRename, onPoints, onRemove }) {
  const [text, setText] = useState(label);
  useEffect(() => setText(label), [label]);
  const commit = () => {
    if (text.trim() && text.trim() !== label) onRename(text.trim());
    else setText(label);
  };
  return (
    <div className="crm-score__row">
      <input
        className="crm-score__match"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        aria-label="Match"
      />
      <input
        className={`crm-score__points crm-num${points < 0 ? " is-neg" : ""}`}
        type="number"
        step="1"
        value={points}
        onChange={(e) => onPoints(e.target.value === "" ? 0 : Number(e.target.value))}
        aria-label={`Points for ${label}`}
      />
      <button type="button" className="crm-score__trash" onClick={onRemove} title="Remove row" aria-label={`Remove ${label}`}>
        ×
      </button>
    </div>
  );
}

/* ----------------------------------------------------------- a group */

function Group({ n, name, group, multiplier, open, onToggle, onChange, onMultiplier }) {
  const entries = Object.entries(group.values);
  const setValues = (values) => onChange({ ...group, values });

  const rename = (from, to) => {
    if (group.values[to] !== undefined) return; // a label may appear once
    const values = {};
    for (const [key, value] of entries) values[key === from ? to : key] = value;
    setValues(values);
  };
  const addRow = () => {
    let i = 1;
    while (group.values[`New row ${i}`] !== undefined) i++;
    setValues({ ...group.values, [`New row ${i}`]: 0 });
  };

  return (
    <div className="crm-score__group">
      <div className="crm-score__grouphead">
        <button type="button" className="crm-score__groupname" onClick={onToggle} aria-expanded={open}>
          <span className="crm-score__n crm-num">{n}</span>
          <Glyph name={open ? "chevronDown" : "chevronRight"} size={15} />
          <span className="crm-score__nametext">
            <b className={group.weight ? "" : "is-off"}>{LABELS[name] || name}</b>
            <span>{ruleLine(name, group, multiplier)}</span>
          </span>
        </button>
        <div className="crm-score__weight">
          <input
            type="range"
            min="0"
            max="10"
            step="1"
            value={group.weight}
            onChange={(e) => onChange({ ...group, weight: Number(e.target.value) })}
            aria-label={`${LABELS[name]} weight`}
          />
          <span className="crm-num">{group.weight ? `×${group.weight}` : "Off"}</span>
        </div>
      </div>
      {open && (
        <div className="crm-score__rows">
          <div className="crm-score__rowhead">
            <span>Match</span>
            <span>Points</span>
            <span />
          </div>
          {entries.map(([label, points]) => (
            <Row
              key={label}
              label={label}
              points={points}
              onRename={(to) => rename(label, to)}
              onPoints={(p) => setValues({ ...group.values, [label]: p })}
              onRemove={() => {
                const values = { ...group.values };
                delete values[label];
                setValues(values);
              }}
            />
          ))}
          <div className="crm-score__groupfoot">
            <button type="button" className="crm-score__add" onClick={addRow}>
              + Add row
            </button>
            {name === "title" && (
              <label className="crm-score__mult">
                <input type="checkbox" checked={multiplier} onChange={(e) => onMultiplier(e.target.checked)} />
                Seniority multiplier <span>C-level and VP count for more, managers for less</span>
              </label>
            )}
            {name === "keywords" && (
              <span className="crm-score__cap">
                Cap
                <span className="crm-score__stepper">
                  <button type="button" onClick={() => onChange({ ...group, cap: Math.max(1, (group.cap || 10) - 1) })} aria-label="Lower cap">
                    −
                  </button>
                  <span className="crm-num">{group.cap || 10}</span>
                  <button type="button" onClick={() => onChange({ ...group, cap: Math.min(30, (group.cap || 10) + 1) })} aria-label="Raise cap">
                    +
                  </button>
                </span>
              </span>
            )}
          </div>
          {NOTES[name] && <p className="crm-score__note">{NOTES[name]}</p>}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------- never a lead */

function NeverALead({ phrases, onChange }) {
  const [text, setText] = useState("");
  const add = () => {
    const phrase = text.trim().toLowerCase();
    if (phrase && !phrases.includes(phrase)) onChange([...phrases, phrase]);
    setText("");
  };
  return (
    <section className="crm-score__card">
      <div className="crm-score__cardhead">
        <h3>Never a lead</h3>
        <span>A message with any of these scores 0, whatever else it has. A job applicant is not a weak lead.</span>
      </div>
      <div className="crm-score__chips">
        {phrases.map((p) => (
          <span key={p} className="crm-score__chip">
            {p}
            <button type="button" onClick={() => onChange(phrases.filter((x) => x !== p))} aria-label={`Remove ${p}`}>
              ×
            </button>
          </span>
        ))}
        <input
          className="crm-score__chipinput"
          value={text}
          placeholder="Add a phrase"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          onBlur={add}
        />
      </div>
    </section>
  );
}

/* ------------------------------------------------------ the bands */

function BandStrip({ cuts, onChange }) {
  const ref = useRef(null);
  const drag = useRef(null);

  useEffect(() => {
    const move = (e) => {
      if (drag.current == null || !ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const i = drag.current;
      const v = Math.round(((e.clientX - rect.left) / rect.width) * 100);
      const next = [...cuts];
      const lo = i ? next[i - 1] + 1 : 1;
      const hi = i < 2 ? next[i + 1] - 1 : 99;
      next[i] = Math.max(lo, Math.min(hi, v));
      if (next[i] !== cuts[i]) onChange(next);
    };
    const up = () => {
      drag.current = null;
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, [cuts, onChange]);

  // Arrow keys on a focused handle, so the cut-offs are not mouse-only.
  const nudge = (i, delta) => {
    const next = [...cuts];
    const lo = i ? next[i - 1] + 1 : 1;
    const hi = i < 2 ? next[i + 1] - 1 : 99;
    next[i] = Math.max(lo, Math.min(hi, next[i] + delta));
    onChange(next);
  };

  const segs = [
    ["Low", 0, cuts[0]],
    ["Medium", cuts[0], cuts[1]],
    ["High", cuts[1], cuts[2]],
    ["Hot", cuts[2], 100],
  ];
  return (
    <div className="crm-score__bands">
      <div className="crm-score__bandshead">
        <b>Priority bands</b>
        <span>What Slack prints for a new lead. Drag to set the cut-offs.</span>
      </div>
      <div className="crm-score__strip" ref={ref}>
        {segs.map(([label, a, b]) => (
          <div
            key={label}
            className={`crm-score__seg crm-score__seg--${label.toLowerCase()}`}
            style={{ width: `${b - a}%` }}
          >
            {b - a >= 8 ? label : ""}
          </div>
        ))}
        {cuts.map((v, i) => (
          <div
            key={i}
            role="slider"
            tabIndex={0}
            aria-label={`${BANDS[i + 1]} from`}
            aria-valuenow={v}
            aria-valuemin={1}
            aria-valuemax={99}
            className="crm-score__handle"
            style={{ left: `${v}%` }}
            onPointerDown={(e) => {
              e.preventDefault();
              drag.current = i;
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowLeft") nudge(i, -1);
              if (e.key === "ArrowRight") nudge(i, 1);
            }}
          >
            <span />
          </div>
        ))}
      </div>
      <div className="crm-score__cutlabels">
        {cuts.map((v, i) => (
          <span key={i} className="crm-num" style={{ left: `${v}%` }}>
            {v}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------ the page */

export default function ScoringPage() {
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [thresholds, setThresholds] = useState([]);
  const [groupOrder, setGroupOrder] = useState(Object.keys(LABELS));
  const [open, setOpen] = useState({ title: true });
  const [cohort, setCohort] = useState("recent");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api("/scoring")
      .then((data) => {
        setSaved(data.scoring);
        setDraft(clone(data.scoring));
        setThresholds(data.thresholds);
        if (data.group_order) setGroupOrder(data.group_order);
      })
      .catch((err) => setError(err.message));
  }, []);

  useEffect(() => {
    if (!draft) return undefined;
    setLoading(true);
    const timer = setTimeout(() => {
      api("/scoring/preview", { method: "POST", body: JSON.stringify({ scoring: draft, cohort, limit: 10 }) })
        .then(setPreview)
        .catch((err) => setPreview({ errors: [err.message] }))
        .finally(() => setLoading(false));
    }, 450);
    return () => clearTimeout(timer);
  }, [draft, cohort]);

  const dirty = useMemo(() => !!saved && !!draft && JSON.stringify(saved) !== JSON.stringify(draft), [saved, draft]);
  const setGroup = useCallback((name, group) => setDraft((d) => ({ ...d, groups: { ...d.groups, [name]: group } })), []);
  const setCuts = useCallback((bands) => setDraft((d) => ({ ...d, bands })), []);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const data = await api("/scoring", { method: "PUT", body: JSON.stringify({ scoring: draft }) });
      setSaved(data.scoring);
      setDraft(clone(data.scoring));
      setThresholds(data.thresholds);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!draft) {
    return (
      <div className="crm-score">
        <p className="crm-score__help">{error || "Loading scoring…"}</p>
      </div>
    );
  }

  const ok = preview && !preview.errors?.length;
  const reply = thresholds[0];
  const replyPass = ok && reply ? preview.passing.find((p) => p.id === reply.id) : null;
  const maxBin = ok ? Math.max(1, ...preview.bins.map((b) => b.count)) : 1;

  const impact = ok
    ? [
        `${preview.moved} of ${preview.total} leads from the last ${preview.days} days change band.`,
        replyPass
          ? `${reply.name} (≥ ${reply.threshold}) would reply to ${replyPass.now}, now ${replyPass.was}.`
          : "",
      ]
        .filter(Boolean)
        .join(" ")
    : "Working out what this moves…";

  return (
    <div className="crm-score">
      {dirty && (
        <div className="crm-score__bar">
          <span>
            <b>Unsaved.</b> {impact}
          </span>
          <button type="button" className="crm-score__barbtn" onClick={() => setDraft(clone(saved))} disabled={saving}>
            Discard
          </button>
          <button type="button" className="crm-score__barbtn crm-score__barbtn--save" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save and re-score"}
          </button>
        </div>
      )}

      <div className="crm-score__body">
        <header className="crm-score__head">
          <div className="crm-score__crumb">
            <span>Inbound</span>
            <Glyph name="chevronRight" size={12} />
            <span>Scoring</span>
          </div>
          <h1>Scoring</h1>
          <p>
            One score per lead, 0–100: how close it is to the best possible lead under these weights. Shows on the
            board card, in the lead's record and as a Leads column, and decides the priority Slack prints.
          </p>
        </header>

        {error && <p className="crm-score__bad">{error}</p>}

        <div className="crm-score__cols">
          <div className="crm-score__main">
            <section className="crm-score__card">
              <div className="crm-score__cardhead crm-score__cardhead--row">
                <h3>Groups</h3>
                <span>Weight 0–10 · 0 turns a group off</span>
              </div>
              {groupOrder.map((name, i) =>
                draft.groups[name] ? (
                  <Group
                    key={name}
                    n={i + 1}
                    name={name}
                    group={draft.groups[name]}
                    multiplier={!!draft.title_seniority_multiplier}
                    open={!!open[name]}
                    onToggle={() => setOpen((o) => ({ ...o, [name]: !o[name] }))}
                    onChange={(group) => setGroup(name, group)}
                    onMultiplier={(v) => setDraft((d) => ({ ...d, title_seniority_multiplier: v }))}
                  />
                ) : null
              )}
            </section>

            <NeverALead
              phrases={draft.negative_keywords}
              onChange={(negative_keywords) => setDraft((d) => ({ ...d, negative_keywords }))}
            />

            <section className="crm-score__card">
              <div className="crm-score__cardhead">
                <span className="crm-score__eyebrow">Used by</span>
                <span>Thresholds stay on their own pages. They're listed so you can see what a change moves.</span>
              </div>
              <div className="crm-score__used">
                {thresholds.map((t) => {
                  const p = ok ? preview.passing.find((x) => x.id === t.id) : null;
                  return (
                    <Link key={t.id} to={`/automations/${t.id}`} className="crm-score__useditem">
                      <div>
                        <b>{t.name}</b>
                        <span>
                          Inbound ▸ Automations · only replies at {t.threshold} or above
                          {p ? ` · ${p.now} of the last ${preview.total} leads pass${dirty && p.now !== p.was ? ` (was ${p.was})` : ""}` : ""}
                        </span>
                      </div>
                      <Glyph name="chevronRight" size={15} />
                    </Link>
                  );
                })}
                <div className="crm-score__useditem">
                  <div>
                    <b>Slack new-lead message</b>
                    <span>Prints the band: hot, high, medium or low</span>
                  </div>
                </div>
                <div className="crm-score__useditem">
                  <div>
                    <b>Board card, lead record, Leads column</b>
                    <span>Sales ▸ Pipeline and Leads · display only</span>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <aside className="crm-score__side">
            <section className={`crm-score__card${loading ? " is-loading" : ""}`}>
              <div className="crm-score__cardhead crm-score__cardhead--row">
                <span className="crm-score__eyebrow">
                  Last {ok ? preview.days : 90} days{ok ? ` · ${preview.total} leads` : ""}
                </span>
                <span>Scored with the weights on screen</span>
              </div>
              {preview?.errors?.length ? (
                <p className="crm-score__bad crm-score__pad">{preview.errors.join(" ")}</p>
              ) : (
                <div className="crm-score__hist">
                  <div className="crm-score__bars">
                    {(ok ? preview.bins : Array.from({ length: 20 }, (_, i) => ({ from: i * 5, count: 0 }))).map((b) => (
                      <div
                        key={b.from}
                        title={`${b.from}–${b.from + 4}: ${b.count} leads`}
                        style={{ height: `${(b.count / maxBin) * 100}%`, background: BAR[bandOf(b.from + 2, draft.bands)] }}
                      />
                    ))}
                    {draft.bands.map((v) => (
                      <i key={v} className="crm-score__cut" style={{ left: `${v}%` }} />
                    ))}
                    {reply && (
                      <>
                        <i className="crm-score__gate" style={{ left: `${reply.threshold}%` }} />
                        <span className="crm-score__gatelabel" style={{ left: `${reply.threshold}%` }}>
                          After-hours ≥ {reply.threshold}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="crm-score__scale crm-num">
                    <span>0</span>
                    <span>25</span>
                    <span>50</span>
                    <span>75</span>
                    <span>100</span>
                  </div>
                  <BandStrip cuts={draft.bands} onChange={setCuts} />
                  {ok && (
                    <p className="crm-score__help">
                      {BANDS.map((b) => `${preview.bands[b.toLowerCase()]} ${b.toLowerCase()}`).join(" · ")}
                      {preview.blocked ? ` · ${preview.blocked} never a lead` : ""}
                    </p>
                  )}
                </div>
              )}
            </section>

            <section className="crm-score__card">
              <div className="crm-score__cardhead crm-score__cardhead--row">
                <span className="crm-score__eyebrow">Re-scored</span>
                <span>Change vs. current score</span>
              </div>
              <div className="crm-score__pills">
                {COHORTS.map((c) => (
                  <button key={c.key} type="button" className={c.key === cohort ? "is-on" : ""} onClick={() => setCohort(c.key)}>
                    {c.label}
                  </button>
                ))}
              </div>
              {ok && !preview.leads.length && <p className="crm-score__help crm-score__pad">No leads in this group yet.</p>}
              {ok &&
                preview.leads.map((l) => {
                  const d = l.score - l.was;
                  return (
                    <div key={l.contact_id} className="crm-score__lead">
                      <div className="crm-score__leadtext">
                        <div>
                          <b>{l.company || l.name}</b>
                          <span>
                            {l.name}
                            {l.job_title ? ` · ${l.job_title}` : ""}
                          </span>
                        </div>
                        <span className="crm-score__chipline crm-num">
                          {l.blocked_reason
                            ? `Never a lead: ${l.blocked_reason}`
                            : l.chips.map((c) => `${c.label} ${c.points > 0 ? "+" : c.points < 0 ? "−" : ""}${Math.abs(c.points)}`).join(" · ") || "No matches"}
                        </span>
                      </div>
                      <span className={`crm-score__delta crm-num${d > 0 ? " is-up" : d < 0 ? " is-down" : ""}`}>
                        {d ? `${d > 0 ? "+" : "−"}${Math.abs(d)}` : "—"}
                      </span>
                      <span className={`crm-score__band crm-score__band--${l.band}`}>{l.band}</span>
                      <span className="crm-score__total crm-num">{l.score}</span>
                    </div>
                  );
                })}
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
}
